import { ApiError } from '@platform/api';
import { captureException, withScope } from '@platform/observability';
import type { SentryFlow } from '../sentry-flow';
import { enrichApiErrorScope } from './enrich-api-error-scope';

export type ExtractApiErrorCodeOptions<TCode extends string> = {
  flow: SentryFlow;
  // Matched against the last URI segment of the RFC 9457 `type` field — the
  // stable slug. Checked before byStatus so it wins when both could apply.
  byCode?: ReadonlySet<TCode>;
  byStatus?: Readonly<Partial<Record<number, TCode>>>;
};

// Maps an unknown error to a feature-specific code. On a known match, returns the code with
// no Sentry traffic. On null (unmapped), captures once here instead of at every call site.
export function extractApiErrorCode<TCode extends string>(error: unknown, opts: ExtractApiErrorCodeOptions<TCode>): TCode | null {
  if (error instanceof ApiError) {
    if (opts.byCode) {
      const slug = lastURISegment(error.data.type);
      if (slug !== '' && opts.byCode.has(slug as TCode)) {
        return slug as TCode;
      }
    }
    const code = opts.byStatus?.[error.status];
    if (code !== undefined) {
      return code;
    }
  }
  withScope((scope) => {
    scope.setTag('flow', opts.flow);
    if (error instanceof ApiError) {
      enrichApiErrorScope(error, scope);
    }
    captureException(error);
  });
  return null;
}

function lastURISegment(uri: string | undefined): string {
  if (!uri) {
    return '';
  }
  const i = uri.lastIndexOf('/');
  return i >= 0 ? uri.slice(i + 1) : uri;
}
